import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Play, ChevronRight, Cpu, HardDrive, Network, Database,
  Layers, ShieldAlert, Activity, Brain, CheckCircle2, Plug, Sparkles, Users,
} from "lucide-react";
import PageMeta from "../components/PageMeta";
import GalleryCarousel from "../components/ScreenshotCarousel";
import {
  NexusArchitectureFlow, NexusTelemetryVisual, NexusAlarmConsole,
  NexusRuleBuilder, NexusNexiChat, NexusCommsRange, NexusDashboardCanvas,
  NexusHardwareShowcase, NexusIndustryRail,
  NexusIndustrySelector, NexusRolesGrid, NexusUserOrgVisual,
} from "../components/NexusVisuals";
import {
  NEXUS_PILLARS, NEXUS_COMMS, NEXUS_POWER, NEXUS_PROTOCOLS, NEXUS_AI,
  NEXUS_INDUSTRIES, NEXUS_HIERARCHY, NEXUS_INTEGRATIONS,
} from "../data/nexusData";
import { NEXUS_INDUSTRY_DETAIL, NEXUS_ROLES, NEXUS_PRODUCT_CALLOUTS } from "../data/nexusIndustryData";
import { NEXUS_DEMO_URL } from "../lib/urls";

const NEXUS_GALLERY_DARK = [
  { src: "/screenshots/nexus/dashboard.jpg",     label: "Live dashboard · pumps, pressures, tanks, flow",       mode: "dark" },
  { src: "/screenshots/nexus/nodes.jpg",         label: "Nodes inventory · 12 nodes across the irrigation site", mode: "dark" },
  { src: "/screenshots/nexus/gateways.jpg",      label: "Gateways · uplink status, RSSI, firmware",             mode: "dark" },
  { src: "/screenshots/nexus/rules.jpg",         label: "Rules · authored by Nexi, dry-run, deploy",            mode: "dark" },
  { src: "/screenshots/nexus/irrigation.jpg",    label: "Irrigation · zones, controllers, dosing, schedules",   mode: "dark" },
  { src: "/screenshots/nexus/sites.jpg",         label: "Sites · multi-site fleet with health scoring",         mode: "dark" },
  { src: "/screenshots/nexus/integrations.jpg",  label: "Integrations · cameras, weather, irrigation, more",    mode: "dark" },
  { src: "/screenshots/nexus/organizations.jpg", label: "Organisations · multi-tenant by design",               mode: "dark" },
];

const NEXUS_GALLERY_LIGHT = [
  { src: "/screenshots/nexus/light/dashboard.jpg",     label: "Live dashboard · pumps, pressures, tanks, flow",       mode: "light" },
  { src: "/screenshots/nexus/light/nodes.jpg",         label: "Nodes inventory · 12 nodes across the irrigation site", mode: "light" },
  { src: "/screenshots/nexus/light/gateways.jpg",      label: "Gateways · uplink status, RSSI, firmware",             mode: "light" },
  { src: "/screenshots/nexus/light/rules.jpg",         label: "Rules · authored by Nexi, dry-run, deploy",            mode: "light" },
  { src: "/screenshots/nexus/light/irrigation.jpg",    label: "Irrigation · zones, controllers, dosing, schedules",   mode: "light" },
  { src: "/screenshots/nexus/light/sites.jpg",         label: "Sites · multi-site fleet with health scoring",         mode: "light" },
  { src: "/screenshots/nexus/light/integrations.jpg",  label: "Integrations · cameras, weather, irrigation, more",    mode: "light" },
  { src: "/screenshots/nexus/light/organizations.jpg", label: "Organisations · multi-tenant by design",               mode: "light" },
];

export default function NexusHub({ onTrialClick }) {
  const [galleryMode, setGalleryMode] = useState("dark");
  const galleryShots = galleryMode === "light" ? NEXUS_GALLERY_LIGHT : NEXUS_GALLERY_DARK;
  return (
    <div className="min-h-screen" data-testid="nexus-hub">
      <PageMeta
        title="Geminii Nexus"
        description="Real-time cloud control for operational technology. Sense any signal, on any protocol, anywhere — and act on it from a browser."
        path="/nexus"
      />

      {/* ============================== HERO ============================== */}
      <section data-testid="nexus-hero" className="relative pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(/images/hero-rogue.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-geminii-deep/70 via-geminii-deep/90 to-geminii-deep" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6"
          >
            <img src="/logos/nexus/white-blue.png" alt="Geminii Nexus" className="h-10 sm:h-12 w-auto" data-testid="nexus-hero-logo" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-nexus-blue/10 border border-nexus-blue/25 mb-6"
          >
            <Sparkles size={11} className="text-nexus-blue" />
            <span className="text-[11px] font-mono text-nexus-blue uppercase tracking-wider">Real-time cloud control · Operational technology, online</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight max-w-4xl"
          >
            Run the physical world <span className="text-nexus-blue">from a browser tab.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-6 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed"
          >
            Geminii Nexus is a real-time control platform for the operations that keep critical systems alive — water, power, mining, manufacturing, built environment. Connect any sensor on any protocol across any distance. Drag live gauges, pump controls, and flow meters onto a canvas built by the operator, not a consultant. Author safety logic in plain English. Let the platform act on its own when the operation can't wait for a human.
          </motion.p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={NEXUS_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nexus-hero-demo"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-nexus-blue text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(30,159,188,0.4)] transition-all duration-250"
            >
              <Play size={14} className="fill-geminii-deep" />
              View live demo
            </a>
            <a
              href="#industries"
              data-testid="nexus-hero-industries"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-nexus-blue/50 hover:text-nexus-blue transition-all duration-250"
            >
              See industries served <ChevronRight size={14} />
            </a>
          </div>

          {/* Headline metrics */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { k: "Telemetry latency", v: "Sub-second", sub: "Node to operator" },
              { k: "Long-range mesh", v: "15 km+", sub: "Per LoRa hop" },
              { k: "Offline runtime", v: "Indefinite", sub: "Edge buffered" },
              { k: "Protocols", v: "Universal", sub: "Modbus · 4-20 mA · Pulse · Serial" },
            ].map((m) => (
              <div
                key={m.k}
                className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4"
              >
                <p className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider">{m.k}</p>
                <p className="font-display font-bold text-xl text-white mt-1">{m.v}</p>
                <p className="text-[10px] text-geminii-dim mt-0.5">{m.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== REAL PRODUCT SHOWCASE ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]" data-testid="nexus-product-shot">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div className="max-w-2xl">
              <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Inside the platform</span>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-white mt-2">A canvas the operator builds — and runs.</h2>
              <p className="text-geminii-muted mt-2 leading-relaxed">
                Tabs for every area of the operation. Live channels polling every five seconds. Gauges, tanks, flow meters, and pump controls on a single screen. Density toggles for the operators who want it dense and the executives who want it roomy.
              </p>
            </div>
            <a
              href={NEXUS_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nexus-product-shot-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-nexus-blue/40 text-nexus-blue font-semibold text-sm hover:bg-nexus-blue/10 hover:border-nexus-blue/70 transition-all"
            >
              <Play size={13} className="fill-nexus-blue" />
              Step into the live demo
            </a>
          </div>

          <div className="relative rounded-2xl border border-white/[0.08] overflow-hidden bg-geminii-card group">
            <div className="absolute top-3 left-4 z-10 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
              <span className="ml-3 text-[10px] font-mono text-geminii-dim uppercase tracking-wider">demo-nexus.geminii-ai.com / portal</span>
            </div>
            <img
              src="/screenshots/nexus/dashboard.jpg"
              alt="Geminii Nexus portal — live widget canvas with pumps, reservoirs, gauges, and flow meters"
              data-testid="nexus-product-shot-image"
              className="w-full h-auto block"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-geminii-deep/40 to-transparent" />
          </div>

          {/* Marketing-led detail callouts */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="nexus-product-shot-detail">
            {NEXUS_PRODUCT_CALLOUTS.map((c) => (
              <div key={c.label} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                <p className="text-[10px] font-mono text-geminii-dim uppercase tracking-wider">{c.label}</p>
                <p className="font-display font-bold text-lg text-white mt-1">{c.value}</p>
                <p className="text-[11px] text-geminii-dim mt-1 leading-relaxed">{c.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== PLATFORM TOUR GALLERY ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]" data-testid="nexus-tour">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div className="max-w-2xl">
              <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Tour the platform</span>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-white mt-2 mb-2">Eight screens. One operating picture.</h2>
              <p className="text-geminii-muted leading-relaxed">
                Browse the views operators use every day — the live dashboard, the rules engine, the node and gateway fleet, the irrigation overview, the integration library, and the multi-tenant org switcher. Click any frame to enlarge.
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Theme toggle */}
              <div
                role="tablist"
                aria-label="Gallery theme"
                data-testid="gallery-theme-toggle"
                className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.04] border border-white/[0.06]"
              >
                {["dark", "light"].map((m) => {
                  const active = galleryMode === m;
                  return (
                    <button
                      key={m}
                      role="tab"
                      aria-selected={active}
                      data-testid={`gallery-theme-${m}`}
                      onClick={() => setGalleryMode(m)}
                      className={`px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-wider transition-all ${
                        active
                          ? "bg-nexus-blue text-geminii-deep shadow-[0_0_12px_rgba(30,159,188,0.4)]"
                          : "text-geminii-muted hover:text-white"
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
              <a
                href={NEXUS_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nexus-tour-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-nexus-blue/40 text-nexus-blue font-semibold text-sm hover:bg-nexus-blue/10 hover:border-nexus-blue/70 transition-all"
              >
                <Play size={13} className="fill-nexus-blue" />
                Open the live demo
              </a>
            </div>
          </div>
          <GalleryCarousel key={galleryMode} screenshots={galleryShots} domain="demo-nexus.geminii-ai.com" />
        </div>
      </section>


      {/* ============================== INDUSTRIES SELECTOR ============================== */}
      <section id="industries" className="py-10 md:py-14 border-t border-white/[0.04]" data-testid="nexus-industries-deep">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl mb-8">
            <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Built for every operator who measures the world</span>
            <h2 className="font-display font-bold text-2xl md:text-3xl text-white mt-2 mb-3">Pick the industry. See exactly how it runs on Geminii Nexus.</h2>
            <p className="text-geminii-muted leading-relaxed">
              The platform is one stack — the application of it is unique to every operation. Open any industry below for the typical assets, the sensor stack, the rule patterns, and the analytics outputs operators in that sector run every day.
            </p>
          </div>
          <NexusIndustrySelector industries={NEXUS_INDUSTRY_DETAIL} />
        </div>
      </section>

      {/* ============================== ROLES & USER MANAGEMENT ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]" data-testid="nexus-roles-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 mb-2">
                <Users size={16} className="text-nexus-blue" />
                <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Roles & user management</span>
              </div>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">Bring the team in. Keep the right hands on the right channels.</h2>
              <p className="text-geminii-muted leading-relaxed mb-8 max-w-2xl">
                The owner sets the perimeter; admins build out the operation; operators watch and act; technicians fix; analysts learn from it; clients see only what's been shared with them. Every role has its own scope across organisations, sites, gateways, and dashboards — and every action is signed into the audit log.
              </p>
              <NexusRolesGrid roles={NEXUS_ROLES} />
            </div>
            <div className="lg:col-span-5 lg:pt-12">
              <NexusUserOrgVisual />
              <p className="text-[11px] font-mono text-geminii-dim uppercase tracking-wider mt-4">
                Invite by email · scope per site · revoke instantly
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* ============================== ARCHITECTURE ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <NexusArchitectureFlow />
        </div>
      </section>

      {/* ============================== PILLARS ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]" data-testid="nexus-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">Built for the operations that can't go offline.</h2>
          <p className="text-geminii-muted mb-10 max-w-2xl">Every architectural choice is biased toward continuity, latency, and operator authority. The platform is fast, opinionated, and runs without supervision.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {NEXUS_PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-geminii-card border border-white/[0.06] hover:border-nexus-blue/30 rounded-xl p-6 transition-colors"
              >
                <p.icon size={22} className="text-nexus-blue mb-4" />
                <h3 className="font-display font-bold text-base text-white mb-2">{p.title}</h3>
                <p className="text-sm text-geminii-muted leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== LIVE TELEMETRY + ALARMS ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">Every channel, live. Every five seconds.</h2>
          <p className="text-geminii-muted mb-10 max-w-2xl">A streaming channel database keeps every reading flowing to every widget. Alarms prioritise themselves, acknowledge with a click, and audit themselves into a tamper-proof timeline.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NexusTelemetryVisual />
            <NexusAlarmConsole />
          </div>
        </div>
      </section>

      {/* ============================== DASHBOARDS ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Operator-built widget canvas</span>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-white mt-2 mb-4">Tabs. Widgets. Channels. Built by the operator.</h2>
              <p className="text-geminii-muted leading-relaxed mb-5">
                Spin up a tab for every area of the operation — Field 3, Pump House 5, Tanks. Drag a pressure gauge, a flow meter, a tank visual, a pump control onto the canvas. Bind each to a live channel. Toggle the canvas between Live, Roomy, Standard, and Dense — the same screen serves both the control room wall and the operations director's laptop.
              </p>
              <ul className="space-y-2.5">
                {[
                  "Multi-tenant data model — organisations, sites, gateways, nodes, channels",
                  "Widget library — gauges, tanks, flow meters, pump controls, KPIs, history charts",
                  "Density toggles — LIVE / ROOMY / STD / DENSE for every viewer",
                  "Channel bindings — CH1 pressure, RELAY1 pump state, no integration tax",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-white">
                    <CheckCircle2 size={14} className="text-nexus-blue mt-1 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <NexusDashboardCanvas />
          </div>
        </div>
      </section>

      {/* ============================== AI CO-PILOT ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]" data-testid="nexus-ai-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-2 mb-2">
            <Brain size={18} className="text-cerebrum-violet" />
            <span className="text-[10px] font-mono text-cerebrum-violet uppercase tracking-wider">Nexi · Cerebrum, speaking SCADA</span>
          </div>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">An AI co-pilot for the people in the control room.</h2>
          <p className="text-geminii-muted mb-10 max-w-2xl">
            Conversational rule authoring, ranked risk findings, anomaly explanations, and generated reports — all reasoning over the same live tag stream the dashboards render. Operators ask in plain English; the platform answers with data, charts, and recommended actions.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <NexusRuleBuilder />
            <NexusNexiChat />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {NEXUS_AI.map((a, i) => (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="bg-geminii-card border border-cerebrum-violet/15 rounded-xl p-5"
              >
                <a.icon size={18} className="text-cerebrum-violet mb-3" />
                <h4 className="font-display font-bold text-sm text-white mb-1.5">{a.title}</h4>
                <p className="text-xs text-geminii-muted leading-relaxed">{a.copy}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== CONNECTIVITY ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Connectivity</span>
              <h2 className="font-display font-bold text-2xl md:text-3xl text-white mt-2 mb-4">Wireless. Wired. Whatever the site demands.</h2>
              <p className="text-geminii-muted leading-relaxed mb-6">
                A field with one strong cellular bar and a plant with thick concrete walls need different radios. Nexus runs all of them. Mix protocols within a single gateway. Reach across kilometres of bush with a LoRa repeater chain. Failover to cellular the moment the primary link blinks.
              </p>
              <div className="space-y-3">
                {NEXUS_COMMS.map((c) => (
                  <div key={c.name} className="flex items-start gap-3 bg-white/[0.02] border border-white/[0.06] rounded-lg p-3.5">
                    <c.icon size={18} className="text-nexus-blue flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="font-display font-bold text-sm text-white">{c.name}</span>
                        <span className="text-[10px] font-mono text-geminii-dim">{c.range}</span>
                      </div>
                      <p className="text-xs text-geminii-muted mt-0.5">{c.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:pt-12">
              <NexusCommsRange />
            </div>
          </div>
        </div>
      </section>

      {/* ============================== POWER & PROTOCOLS ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Power */}
            <div>
              <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Power</span>
              <h2 className="font-display font-bold text-2xl text-white mt-2 mb-4">Off-grid by design, mains-friendly by choice.</h2>
              <p className="text-geminii-muted leading-relaxed mb-5">Field nodes ship in three power profiles — pick the one the site supports.</p>
              <div className="space-y-3">
                {NEXUS_POWER.map((p) => (
                  <div key={p.name} className="flex items-start gap-3 bg-white/[0.02] border border-white/[0.06] rounded-lg p-3.5">
                    <p.icon size={18} className="text-nexus-blue flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-display font-bold text-sm text-white">{p.name}</span>
                        <span className="text-[10px] font-mono text-geminii-dim">{p.spec}</span>
                      </div>
                      <p className="text-xs text-geminii-muted mt-0.5">{p.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Protocols */}
            <div>
              <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Signal capture</span>
              <h2 className="font-display font-bold text-2xl text-white mt-2 mb-4">Every protocol on the plant floor. One tag database.</h2>
              <p className="text-geminii-muted leading-relaxed mb-5">A single node ingests legacy serial, modern industrial buses, analog standards, and digital I/O — and presents them as the same first-class tags.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {NEXUS_PROTOCOLS.map((p) => (
                  <div key={p.name} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
                    <p className="font-display font-bold text-xs text-white mb-1">{p.name}</p>
                    <p className="text-[11px] text-geminii-muted leading-relaxed">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================== TENANCY / HIERARCHY ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">Built for fleets. From the first site.</h2>
          <p className="text-geminii-muted mb-10 max-w-2xl">A multi-tier data model means a single tenant can scale from one pump house to a national fleet without re-architecting. Permissions, branding, and dashboards travel with the hierarchy.</p>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {NEXUS_HIERARCHY.map((h, i) => (
              <motion.div
                key={h.tier}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-geminii-card border border-white/[0.06] rounded-xl p-5"
              >
                <p className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Tier {i + 1}</p>
                <h3 className="font-display font-bold text-base text-white mt-1.5 mb-1.5">{h.tier}</h3>
                <p className="text-xs text-geminii-muted leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== INTEGRATIONS ============================== */}
      <section className="py-10 md:py-14 border-t border-white/[0.04]" data-testid="nexus-integrations">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-2 mb-2">
            <Plug size={16} className="text-nexus-blue" />
            <span className="text-[10px] font-mono text-nexus-blue uppercase tracking-wider">Integrations</span>
          </div>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-white mb-3">Stop bouncing between tabs.</h2>
          <p className="text-geminii-muted mb-8 max-w-2xl">External APIs flow into the same dashboards as the telemetry — weather, tariffs, maps, ticket systems, identity providers, warehouses. The control room becomes the single pane of glass.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {NEXUS_INTEGRATIONS.map((label, i) => {
              const [head, ...rest] = label.split(" — ");
              return (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-geminii-card border border-white/[0.06] rounded-lg p-4"
                >
                  <p className="font-display font-bold text-xs text-white mb-1">{head}</p>
                  <p className="text-[11px] text-geminii-muted leading-relaxed">{rest.join(" — ")}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================== CLOSING CTA ============================== */}
      <section className="py-14 md:py-20 border-t border-white/[0.04]" data-testid="nexus-closing">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <img src="/logos/nexus/white-blue.png" alt="Geminii Nexus" className="h-9 w-auto mx-auto mb-6 opacity-80" />
          <h2 className="font-display font-bold text-3xl md:text-4xl text-white mb-4 leading-tight">
            Bring the operation online.
          </h2>
          <p className="text-geminii-muted mb-8 max-w-xl mx-auto leading-relaxed">
            A live demo tenant is open to the public — no signup, no credentials, just the platform running on a real dataset. Step inside before talking to anyone.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={NEXUS_DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nexus-closing-demo"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-nexus-blue text-geminii-deep font-semibold text-sm hover:shadow-[0_0_24px_rgba(30,159,188,0.4)] transition-all"
            >
              <Play size={14} className="fill-geminii-deep" />
              Open the live demo
            </a>
            <Link
              to="/contact?intent=nexus"
              data-testid="nexus-closing-contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-white/[0.12] text-white font-semibold text-sm hover:border-nexus-blue/50 hover:text-nexus-blue transition-all"
            >
              Get in touch <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
